import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { UserBasic, userService } from "../../services/userService";

/** The email, marked when the account has since been deactivated. */
const label = (user: UserBasic | null) =>
    user ? `${user.email}${user.is_active === false ? " (deactivated)" : ""}` : null;

interface AuditTrailProps {
    createdByUserId?: string;
    updatedByUserId?: string;
}

/**
 * "Created by X · Last edited by Y" — resolves user ids to emails on
 * mount via userService (which caches per session). Omits the "last
 * edited" half when it's the same person who created it, or when there's
 * no updated_by at all (e.g. never touched since creation, or a
 * delivery-webhook update with no staff member to attribute).
 */
export const AuditTrail: React.FC<AuditTrailProps> = ({
    createdByUserId,
    updatedByUserId,
}) => {
    const [createdByEmail, setCreatedByEmail] = useState<string | null>(null);
    const [updatedByEmail, setUpdatedByEmail] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setCreatedByEmail(null);
        if (createdByUserId) {
            userService.getUserBasic(createdByUserId).then((user) => {
                if (!cancelled) setCreatedByEmail(label(user));
            });
        }
        return () => {
            cancelled = true;
        };
    }, [createdByUserId]);

    useEffect(() => {
        let cancelled = false;
        setUpdatedByEmail(null);
        if (updatedByUserId && updatedByUserId !== createdByUserId) {
            userService.getUserBasic(updatedByUserId).then((user) => {
                if (!cancelled) setUpdatedByEmail(label(user));
            });
        }
        return () => {
            cancelled = true;
        };
    }, [updatedByUserId, createdByUserId]);

    if (!createdByUserId && !updatedByUserId) return null;

    const parts: string[] = [];
    if (createdByUserId) parts.push(`Created by ${createdByEmail ?? "…"}`);
    if (updatedByUserId && updatedByUserId !== createdByUserId) {
        parts.push(`Last edited by ${updatedByEmail ?? "…"}`);
    }
    if (parts.length === 0) return null;

    return (
        <Typography variant="caption" color="text.secondary" display="block">
            {parts.join(" · ")}
        </Typography>
    );
};
